/** Server-funktioner för jobbannonser, shortlists och genomförda intervjuer. */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { cached, invalidateCached } from "@/lib/server-cache";
import {
  JOB_AD_STATUSES,
  slugifyAd,
  type CompletedInterview,
  type JobAd,
  type JobAdStatus,
  type JobShortlist,
} from "@/lib/job-ads";

type Row = Record<string, unknown>;

type Ctx = {
  supabase: {
    from: (table: string) => never;
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
  };
  userId: string;
  claims?: unknown;
};

async function assertJobAccess(context: Ctx): Promise<void> {
  const { data, error } = await context.supabase.rpc("can_manage_jobs", {
    _user_id: context.userId,
  });
  if (error || data !== true) throw new Error("Du saknar behörighet till rekryteringen.");
}

function str(row: Row, key: string): string {
  return String(row[key] ?? "");
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}

function mapAd(row: Row): JobAd {
  const status = str(row, "status") as JobAdStatus;
  return {
    id: str(row, "id"),
    slug: str(row, "slug"),
    positionSlug: str(row, "position_slug"),
    title: { sv: str(row, "title_sv"), en: str(row, "title_en"), es: str(row, "title_es") },
    lead: { sv: str(row, "lead_sv"), en: str(row, "lead_en"), es: str(row, "lead_es") },
    body: { sv: str(row, "body_sv"), en: str(row, "body_en"), es: str(row, "body_es") },
    extent: str(row, "extent"),
    location: str(row, "location"),
    contact: str(row, "contact"),
    startsAt: row["starts_at"] ? String(row["starts_at"]) : null,
    applyBy: row["apply_by"] ? String(row["apply_by"]) : null,
    requirements: strings(row["requirements"]),
    merits: strings(row["merits"]),
    customFields: Array.isArray(row["custom_fields"])
      ? (row["custom_fields"] as Row[]).map((f) => ({
          label: String(f["label"] ?? ""),
          value: String(f["value"] ?? ""),
          public: f["public"] !== false,
        }))
      : [],
    status: JOB_AD_STATUSES.includes(status) ? status : "draft",
    sortOrder: Number(row["sort_order"] ?? 0),
    createdAt: str(row, "created_at"),
    updatedAt: str(row, "updated_at"),
  };
}

/** Publikt: publicerade annonser till /lediga-tjanster. */
export const listPublishedJobAds = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ ads: JobAd[] }> => cached("public-job-ads", 5 * 60_000, async () => {
    const { publicClient } = await import("@/lib/contact.server");
    const { data } = await publicClient()
      .from("job_ads")
      .select("*")
      .eq("status", "published")
      .order("sort_order", { ascending: true });
    return { ads: ((data ?? []) as Row[]).map(mapAd) };
  }),
);

/** Alla annonser i admin. */
export const listJobAds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ ads: JobAd[] }> => {
    await assertJobAccess(context as never);
    const { data, error } = await (context.supabase.from("job_ads") as never as {
      select: (q: string) => {
        order: (c: string, o: { ascending: boolean }) => Promise<{ data: Row[] | null; error: unknown }>;
      };
    })
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw new Error("Kunde inte hämta annonserna.");
    return { ads: (data ?? []).map(mapAd) };
  });

const adSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().max(60).default(""),
  positionSlug: z.string().trim().max(80).default(""),
  titleSv: z.string().trim().min(2).max(200),
  titleEn: z.string().trim().max(200).default(""),
  titleEs: z.string().trim().max(200).default(""),
  leadSv: z.string().trim().max(600).default(""),
  leadEn: z.string().trim().max(600).default(""),
  leadEs: z.string().trim().max(600).default(""),
  bodySv: z.string().trim().max(12000).default(""),
  bodyEn: z.string().trim().max(12000).default(""),
  bodyEs: z.string().trim().max(12000).default(""),
  extent: z.string().trim().max(80).default(""),
  location: z.string().trim().max(120).default(""),
  contact: z.string().trim().max(200).default(""),
  startsAt: z.string().trim().max(20).default(""),
  applyBy: z.string().trim().max(20).default(""),
  requirements: z.array(z.string().trim().max(300)).max(40).default([]),
  merits: z.array(z.string().trim().max(300)).max(40).default([]),
  customFields: z
    .array(
      z.object({
        label: z.string().trim().max(120),
        value: z.string().trim().max(400),
        public: z.boolean().default(true),
      }),
    )
    .max(20)
    .default([]),
  status: z.enum(JOB_AD_STATUSES).default("draft"),
  sortOrder: z.number().int().min(0).max(999).default(0),
});

/** Skapar eller uppdaterar en annons. */
export const saveJobAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => adSchema.parse(data))
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);

    const payload = {
      slug: data.slug.trim() || `${slugifyAd(data.titleSv)}-${Date.now().toString(36).slice(-4)}`,
      position_slug: data.positionSlug,
      title_sv: data.titleSv,
      title_en: data.titleEn,
      title_es: data.titleEs,
      lead_sv: data.leadSv,
      lead_en: data.leadEn,
      lead_es: data.leadEs,
      body_sv: data.bodySv,
      body_en: data.bodyEn,
      body_es: data.bodyEs,
      extent: data.extent,
      location: data.location,
      contact: data.contact,
      starts_at: data.startsAt || null,
      apply_by: data.applyBy || null,
      requirements: data.requirements.filter(Boolean),
      merits: data.merits.filter(Boolean),
      custom_fields: data.customFields.filter((f) => f.label || f.value),
      status: data.status,
      sort_order: data.sortOrder,
    };

    const table = context.supabase.from("job_ads") as never as {
      insert: (v: unknown) => Promise<{ error: unknown }>;
      update: (v: unknown) => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    };

    const { error } = data.id
      ? await table.update(payload).eq("id", data.id)
      : await table.insert({ ...payload, created_by: context.userId });
    if (error) throw new Error("Annonsen kunde inte sparas.");
    invalidateCached("public-job-ads");

    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: data.id ? "job_ad.update" : "job_ad.create",
      entity: "job_ad",
      entityId: data.id ?? payload.slug,
      summary: `${data.id ? "Ändrade" : "Skapade"} jobbannonsen ${data.titleSv} (${data.status})`,
    });
    return { ok: true as const };
  });

/** Tar bort en annons. */
export const deleteJobAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);
    const { error } = await (context.supabase.from("job_ads") as never as {
      delete: () => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    })
      .delete()
      .eq("id", data.id);
    if (error) throw new Error("Annonsen kunde inte tas bort.");
    invalidateCached("public-job-ads");

    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "job_ad.delete",
      entity: "job_ad",
      entityId: data.id,
      severity: "warning",
      summary: "Tog bort en jobbannons",
    });
    return { ok: true as const };
  });

/** Alla shortlists med kandidat-id. */
export const listJobShortlists = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ shortlists: JobShortlist[] }> => {
    await assertJobAccess(context as never);
    const [lists, members] = await Promise.all([
      context.supabase.from("job_shortlists").select("*").order("created_at", { ascending: false }),
      context.supabase.from("job_shortlist_members").select("*"),
    ]);

    const byList = new Map<string, string[]>();
    for (const m of ((members as { data: Row[] | null }).data ?? []) as Row[]) {
      const key = String(m["shortlist_id"]);
      byList.set(key, [...(byList.get(key) ?? []), String(m["application_id"])]);
    }

    return {
      shortlists: (((lists as { data: Row[] | null }).data ?? []) as Row[]).map((row) => ({
        id: str(row, "id"),
        name: str(row, "name"),
        description: str(row, "description"),
        createdByEmail: str(row, "created_by_email"),
        createdAt: str(row, "created_at"),
        applicationIds: byList.get(str(row, "id")) ?? [],
      })),
    };
  });

/** Skapar eller döper om en shortlist. */
export const saveJobShortlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid().optional(),
        name: z.string().trim().min(2).max(120),
        description: z.string().trim().max(600).default(""),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);
    const email = (context.claims as { email?: string } | undefined)?.email ?? "";
    const table = context.supabase.from("job_shortlists") as never as {
      insert: (v: unknown) => { select: (q: string) => { single: () => Promise<{ data: Row | null; error: unknown }> } };
      update: (v: unknown) => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    };

    if (data.id) {
      const { error } = await table
        .update({ name: data.name, description: data.description })
        .eq("id", data.id);
      if (error) throw new Error("Listan kunde inte sparas.");
      return { ok: true as const, id: data.id };
    }

    const { data: row, error } = await table
      .insert({
        name: data.name,
        description: data.description,
        created_by: context.userId,
        created_by_email: email,
      })
      .select("id")
      .single();
    if (error || !row) throw new Error("Listan kunde inte skapas.");
    return { ok: true as const, id: String(row["id"]) };
  });

/** Tar bort en shortlist. */
export const deleteJobShortlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);
    const members = context.supabase.from("job_shortlist_members") as never as {
      delete: () => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    };
    await members.delete().eq("shortlist_id", data.id);
    const { error } = await (context.supabase.from("job_shortlists") as never as {
      delete: () => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    })
      .delete()
      .eq("id", data.id);
    if (error) throw new Error("Listan kunde inte tas bort.");
    return { ok: true as const };
  });

/** Lägger till eller tar bort kandidater i en shortlist. */
export const updateShortlistMembers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        shortlistId: z.string().uuid(),
        applicationIds: z.array(z.string().uuid()).min(1).max(100),
        remove: z.boolean().default(false),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);
    const table = context.supabase.from("job_shortlist_members") as never as {
      insert: (v: unknown) => Promise<{ error: unknown }>;
      delete: () => {
        eq: (c: string, v: string) => { in: (c: string, v: string[]) => Promise<{ error: unknown }> };
      };
    };

    if (data.remove) {
      const { error } = await table
        .delete()
        .eq("shortlist_id", data.shortlistId)
        .in("application_id", data.applicationIds);
      if (error) throw new Error("Kunde inte ta bort ur listan.");
      return { ok: true as const, count: data.applicationIds.length };
    }

    const { error } = await table.insert(
      data.applicationIds.map((id) => ({ shortlist_id: data.shortlistId, application_id: id })),
    );
    if (error) throw new Error("Kunde inte lägga till i listan.");
    return { ok: true as const, count: data.applicationIds.length };
  });

/** Genomförda intervjuer, med möjlighet att visa arkiverade. */
export const listCompletedInterviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ interviews: CompletedInterview[] }> => {
    await assertJobAccess(context as never);
    const { data: rows } = await context.supabase.from("job_interviews").select("*");
    const list = ((rows ?? []) as Row[]).filter((r) => r["scheduled_at"] || r["outcome"]);
    if (list.length === 0) return { interviews: [] };

    const ids = list.map((r) => str(r, "application_id"));
    const { data: apps } = await (context.supabase.from("job_applications") as never as {
      select: (q: string) => { in: (c: string, v: string[]) => Promise<{ data: Row[] | null }> };
    })
      .select("id, first_name, last_name, position_label")
      .in("id", ids);

    const appById = new Map<string, Row>();
    for (const a of (apps ?? []) as Row[]) appById.set(str(a, "id"), a);

    return {
      interviews: list.map((row) => {
        const app = appById.get(str(row, "application_id"));
        const questions = Array.isArray(row["question_scores"])
          ? (row["question_scores"] as Row[])
          : [];
        const scored = questions.filter((q) => typeof q["score"] === "number");
        return {
          applicationId: str(row, "application_id"),
          name: app ? `${str(app, "first_name")} ${str(app, "last_name")}`.trim() : "Okänd",
          positionLabel: app ? str(app, "position_label") : "",
          scheduledAt: row["scheduled_at"] ? String(row["scheduled_at"]) : null,
          completedAt: row["completed_at"] ? String(row["completed_at"]) : null,
          interviewer: str(row, "interviewer"),
          location: str(row, "location"),
          outcome: str(row, "outcome"),
          archived: row["archived"] === true,
          averageScore:
            scored.length === 0
              ? null
              : Math.round(
                  (scored.reduce((sum, q) => sum + Number(q["score"] ?? 0), 0) / scored.length) * 10,
                ) / 10,
          questionCount: questions.length,
        };
      }),
    };
  });

/** Arkiverar eller återställer en genomförd intervju. */
export const archiveInterview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ applicationId: z.string().uuid(), archived: z.boolean() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertJobAccess(context as never);
    const { error } = await (context.supabase.from("job_interviews") as never as {
      update: (v: unknown) => { eq: (c: string, v: string) => Promise<{ error: unknown }> };
    })
      .update({ archived: data.archived })
      .eq("application_id", data.applicationId);
    if (error) throw new Error("Intervjun kunde inte arkiveras.");
    return { ok: true as const };
  });

/** Publik läsning: publicerade annonser till /lediga-tjanster. */
export const listPublicJobAds = createServerFn({ method: "GET" }).handler(async () => cached("public-job-ads-list", 5 * 60_000, async () => {
  const { createSupabaseServerClient } = await import("@/lib/supabase-public.server");
  const supabase = createSupabaseServerClient();
  const { data, error } = await (supabase.from("job_ads") as never as {
    select: (cols: string) => {
      eq: (c: string, v: string) => {
        order: (c: string, o: { ascending: boolean }) => Promise<{ data: Row[] | null; error: unknown }>;
      };
    };
  })
    .select("*")
    .eq("status", "published")
    .order("sort_order", { ascending: true });
  if (error) return [] as JobAd[];
  return (data ?? []).map(mapAd);
}));
